import { Row, Col, InputGroup, Button, InputGroupText } from "reactstrap";
import React from "react";
import { connect } from "react-redux";
import Dashboard from "../../../components/dashboard";
import { paginateDonationsSuperadmin } from "../../../../utils/fetch";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import DatePicker from "react-datepicker";
import datePickerS from "react-datepicker/dist/react-datepicker.css";
import { Moment } from "moment";
import moment from "moment";
import classNames from "classnames";
import ReactTooltip from "react-tooltip";
import { ExportType } from "../../exportModal";
import ISelectionUpdate from "../../../../types/ISelectionUpdate";
import {
  withModals,
  ModalProps,
  serviceCaller,
  ServiceCallerProps,
  withUserCharities,
  UserCharitiesProps,
  withSuperadminLoginRedirect,
} from "../../../composition";
import { ISortOption } from "../../sortDropdown";
import SortDropdown from "../../sortDropdown";
import ICharity from "../../../../types/services/ICharity";
import { fetchMissingCharityMetadata } from "../../../../utils/charityMetadataPrefetch";
import * as commonActions from "../../../../utils/actions/common";
import IColumnName from "../../../../types/IColumnName";
import IDonation from "../../../../types/services/IDonation";
import IPaginatedResponse from "../../../../types/services/IPaginatedResponse";
import {
  lookupViewSparkLabel,
  trackingPostIdOf,
} from "../../../../utils/viewSparkLabel";
import {
  applyColumnPrefs,
  TableIds,
  toggleSavedColumn,
} from "../../../../utils/tablePrefs";

interface Props extends ServiceCallerProps, ModalProps, UserCharitiesProps {
  refreshDashboard: () => void;
}

interface State {
  sortBy: ISortOption;
  selectedCharity: ISortOption;
  selectAll: boolean;
  sortAsc: boolean;
  startDate: Moment;
  endDate: Moment;
  selectedIds: string[];
  suppressedIds: string[];
  totalResult: number;
  columnNames: Array<IColumnName>;
}

class SuperadminDonationsPage extends React.PureComponent<Props, State> {
  sortOptions: ISortOption[] = [
    { key: "RECEIVED", show: "Received On", colName: "receivedPretty" },
    { key: "AMOUNT", show: "Amount", colName: "amountPretty" },
    { key: "FREQUENCY", show: "Frequency", colName: "frequencyPretty" },
  ];

  charityOptions: ISortOption[] = [{ key: "All", show: "All", colName: "" }];

  state = {
    sortBy: this.sortOptions[0],
    selectedCharity: this.charityOptions[0],
    selectAll: false,
    sortAsc: false,
    startDate: null,
    endDate: null,
    selectedIds: [],
    suppressedIds: [],
    totalResult: 0,
    columnNames: applyColumnPrefs(TableIds.superadminDonations, [
      { key: "Charity", isDisabled: false },
      { key: "Name", isDisabled: false },
      { key: "Company", isDisabled: false },
      { key: "ViewSpark", isDisabled: false, hasCellTooltip: true },
      { key: "Amount", isDisabled: false },
      { key: "Frequency", isDisabled: false },
      { key: "Received On", isDisabled: false },
    ]),
  };

  componentDidMount() {
    if (this.props.charities) {
      this.fillCharityDropdown();
    }
    if (window) {
      this.handleWindowSize();
      window.addEventListener("resize", this.handleWindowSize);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.charities !== prevProps.charities) {
      this.fillCharityDropdown();
    }
  }

  componentWillUnmount() {
    if (window) {
      window.removeEventListener("resize", this.handleWindowSize);
    }
  }

  handleWindowSize = () => {
    if (window) {
      if (window.innerWidth < 768) {
        this.setState({
          columnNames: applyColumnPrefs(TableIds.superadminDonations, [
            { key: "Charity", isDisabled: false },
            { key: "Name", isDisabled: false },
            { key: "Company", isDisabled: true },
            { key: "ViewSpark", isDisabled: false, hasCellTooltip: true },
            { key: "Amount", isDisabled: false },
            { key: "Frequency", isDisabled: false },
            { key: "Received On", isDisabled: false },
          ]),
        });
      } else {
        this.setState({
          columnNames: applyColumnPrefs(TableIds.superadminDonations, [
            { key: "Charity", isDisabled: false },
            { key: "Name", isDisabled: false },
            { key: "Company", isDisabled: false },
            { key: "ViewSpark", isDisabled: false, hasCellTooltip: true },
            { key: "Amount", isDisabled: false },
            { key: "Frequency", isDisabled: false },
            { key: "Received On", isDisabled: false },
          ]),
        });
      }
    }
  };

  disableColumn = index => {
    const copy = toggleSavedColumn(
      TableIds.superadminDonations,
      this.state.columnNames,
      index
    );
    this.setState(
      {
        columnNames: copy,
      },
      () => {
        this.props.columnsModalChange(this.state.columnNames);
      }
    );
  };

  changeColumns = () => {
    this.props.toggleColumnsModal(this.disableColumn, this.state.columnNames);
  };

  fillCharityDropdown() {
    this.charityOptions = [{ key: "All", show: "All", colName: "" }];
    this.props.charities.forEach((charity: ICharity) => {
      if (
        charity.id !== "superadmin" &&
        charity.id !== "abc" &&
        charity.id !== "donation-reader" &&
        !charity.disableGive
      ) {
        this.charityOptions.push({
          key: charity.id,
          show: charity.name || charity.id,
          colName: "",
        });
      }
    });
  }

  private viewSparkLabelCache = new Map<string, Promise<string>>();

  private charityNames = new Map<string, string>();

  getResponse = async (currentPage, pageSize) => {
    const page = await this.fetchDonationsPage(currentPage, pageSize);
    const charityIds = new Set((page.result || []).map(row => row.charityId));
    const charities = await fetchMissingCharityMetadata(
      this.props.charities.filter(charity => charityIds.has(charity.id))
    );
    charities.forEach(charity => {
      if (charity.name) this.charityNames.set(charity.id, charity.name);
    });
    this.props.updateCharities(
      this.props.charities.map(charity => {
        const name = this.charityNames.get(charity.id);
        return name && !charity.name ? { ...charity, name } : charity;
      })
    );
    return this.attachViewSparkLabels(page);
  };

  attachViewSparkLabels = async (
    page: IPaginatedResponse<IDonation>
  ): Promise<IPaginatedResponse<IDonation>> => {
    await Promise.all(
      (page.result || []).map(async donation => {
        const postId = trackingPostIdOf(donation);
        if (!postId || !donation.charityId) {
          donation.viewSparkPretty = "";
          return;
        }
        const key = `${donation.charityId}:${postId}`;
        if (!this.viewSparkLabelCache.has(key)) {
          this.viewSparkLabelCache.set(
            key,
            lookupViewSparkLabel(donation.charityId, postId)
          );
        }
        donation.viewSparkPretty = await this.viewSparkLabelCache.get(key);
      })
    );
    return page;
  };

  fetchDonationsPage = (currentPage, pageSize) => {
    return paginateDonationsSuperadmin(
      this.props.config.searchServiceUrl,
      this.state.selectedCharity.key !== "All"
        ? this.state.selectedCharity.key
        : undefined,
      currentPage,
      pageSize,
      this.state.sortBy.key,
      this.state.sortAsc,
      this.actualStartDate(),
      this.actualEndDate(),
      this.props.serviceAccessToken
    );
  };

  selectAllCheck = e => {
    this.setState({
      selectAll: e.target.checked,
      selectedIds: [],
      suppressedIds: [],
    });
  };

  handleColumnHeaderSort = (accessor: string) => {
    const option = this.sortOptions.find(o => o.colName === accessor);
    if (!option) {
      return;
    }
    this.setState(
      prev => {
        const same = prev.sortBy.colName === option.colName;
        return {
          sortBy: option,
          sortAsc: same ? !prev.sortAsc : false,
        };
      },
      () => this.props.refreshDashboard()
    );
  };

  sortCharityChange = (option: ISortOption) => {
    this.setState(
      {
        selectedCharity: option,
      },
      () => {
        this.props.refreshDashboard();
      }
    );
  };

  startDateChange = e => {
    let startDate = e;
    let endDate = this.state.endDate;
    if (startDate && startDate.isAfter(endDate)) {
      endDate = startDate;
    }
    this.setState({ startDate, endDate, selectAll: false });
  };

  endDateChange = e => {
    let startDate = this.state.startDate;
    let endDate: Moment = e;
    if (startDate && startDate.isAfter(endDate)) {
      endDate = startDate;
    }
    this.setState({ startDate, endDate, selectAll: false });
  };

  actualStartDate() {
    if (this.state.startDate) {
      const startDate = moment(this.state.startDate).startOf("day");
      return startDate.toDate().getTime();
    } else {
      return null;
    }
  }

  actualEndDate() {
    if (this.state.endDate) {
      const endDate = moment(this.state.endDate)
        .add(1, "day")
        .startOf("day");
      return endDate.toDate().getTime();
    } else {
      return null;
    }
  }

  execExport = () => {
    const exportAmount = this.state.selectAll
      ? this.state.totalResult - this.state.suppressedIds.length
      : this.state.selectedIds.length === 0
      ? this.state.totalResult
      : this.state.selectedIds.length;
    this.props.toggleExportModal(
      ExportType.extendedDonations,
      this.state.selectAll ? this.state.suppressedIds : this.state.selectedIds,
      exportAmount.toString(),
      this.actualStartDate(),
      this.actualEndDate(),
      this.state.selectAll || this.state.selectedIds.length === 0,
      null,
      this.state.selectedCharity.key !== "All"
        ? this.state.selectedCharity.key
        : null
    );
  };

  selectionUpdate = (update: ISelectionUpdate) => {
    this.setState({
      totalResult: update.totalAmount,
      selectedIds: update.selectedIds,
      suppressedIds: update.suppressedIds,
    });
  };

  shouldComponentUpdate(_, nextState: State) {
    return (
      (this.state.selectedIds === nextState.selectedIds &&
        this.state.suppressedIds === nextState.suppressedIds) ||
      this.state.selectAll !== nextState.selectAll
    );
  }

  clearStartDate = () => {
    this.setState({ startDate: null });
  };

  clearEndDate = () => {
    this.setState({ endDate: null });
  };

  getCharityName = charityId => {
    let charityName = this.charityNames.get(charityId) || charityId;
    this.props.charities.forEach(charity => {
      if (charity.id === charityId && charity.name) {
        charityName = charity.name;
      }
    });
    return charityName;
  };

  columnNames = [
    "Charity",
    "Name",
    "Company",
    "ViewSpark",
    "Amount",
    "Frequency",
    "Received On",
  ];

  columnData = [
    "charityId",
    "donor.name",
    "donor.company",
    "viewSparkPretty",
    "amountPretty",
    "frequencyPretty",
    "receivedPretty",
  ];

  render() {
    return (
      <main className={s.main}>
        <div className={s.container}>
          <Row className={s.marginBot}>
            <Col xs={12} className={classNames(s.colMargin, s.noPadding)}>
              <div className={s.donationsToolbarFlex}>
                <div className={s.toolbarLeft}>
                  <div
                    className={s.inlineDots}
                    onClick={this.changeColumns}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        this.changeColumns();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    data-tip
                    data-for="superadmin-donations-column-mgmt"
                    aria-label="Column management"
                  >
                    <i className={"fas fa-columns " + s.dotIcon}>&nbsp;</i>
                  </div>
                  <ReactTooltip
                    id="superadmin-donations-column-mgmt"
                    effect="solid"
                    place="top"
                  >
                    Column management
                  </ReactTooltip>
                </div>
                <div className={s.toolbarCharity}>
                  <SortDropdown
                    options={this.charityOptions}
                    sortChanged={this.sortCharityChange}
                  />
                </div>
                <div className={s.toolbarDates}>
                  <div className={s.dateField}>
                    <label className={s.dateLabel} htmlFor="from">
                      From
                    </label>
                    <InputGroup className={classNames(s.noFlex, s.dateInputGroup)}>
                      <InputGroupText className={s.inputGroup}>
                        <i className={"fas fa-calendar " + s.calendarIcon}>
                          &nbsp;
                        </i>
                      </InputGroupText>
                      <DatePicker
                        id="from"
                        selected={this.state.startDate}
                        selectsStart
                        startDate={this.state.startDate}
                        endDate={this.state.endDate}
                        onChange={this.startDateChange}
                        className={s.text}
                        placeholderText={"mm/dd/yyyy"}
                        autoComplete="off"
                      />
                      <InputGroupText className={s.inputGroupRight}>
                        <i
                          className={"fas fa-times " + s.closeIcon}
                          onClick={this.clearStartDate}
                        >
                          &nbsp;
                        </i>
                      </InputGroupText>
                    </InputGroup>
                  </div>
                  <div className={s.dateField}>
                    <label className={s.dateLabel} htmlFor="to">
                      To
                    </label>
                    <InputGroup className={classNames(s.noFlex, s.dateInputGroup)}>
                      <InputGroupText className={s.inputGroup}>
                        <i className={"fas fa-calendar " + s.calendarIcon}>
                          &nbsp;
                        </i>
                      </InputGroupText>
                      <DatePicker
                        id="to"
                        selected={this.state.endDate}
                        selectsEnd
                        startDate={this.state.startDate}
                        endDate={this.state.endDate}
                        onChange={this.endDateChange}
                        className={s.text}
                        placeholderText={"mm/dd/yyyy"}
                        autoComplete="off"
                      />
                      <InputGroupText className={s.inputGroupRight}>
                        <i
                          className={"fas fa-times " + s.closeIcon}
                          onClick={this.clearEndDate}
                        >
                          &nbsp;
                        </i>
                      </InputGroupText>
                    </InputGroup>
                  </div>
                </div>
                <div className={s.toolbarActions}>
                  <Button
                    id="ExportSuperadminDonationsButton"
                    color="primary"
                    className={s.newPrimaryButton}
                    onClick={this.execExport}
                  >
                    Export
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            {this.props.serviceAccessToken && this.props.config && (
              <Dashboard
                tableId={TableIds.superadminDonations}
                columnNames={this.state.columnNames}
                columnData={this.columnData}
                checkCol
                selectAllInCheckboxHeader
                onSelectAllCheckboxChange={this.selectAllCheck}
                suppressedIds={this.state.suppressedIds}
                selectionUpdate={this.selectionUpdate}
                sortBy={this.state.sortBy.colName}
                selectAll={this.state.selectAll}
                sortAsc={this.state.sortAsc}
                columnHeaderSort={{
                  sortAccessor: this.state.sortBy.colName,
                  sortAscending: this.state.sortAsc,
                  sortableAccessors: this.sortOptions.map(o => o.colName),
                  onSort: this.handleColumnHeaderSort,
                }}
                from={this.state.startDate}
                to={this.state.endDate}
                currentCharity={this.props.currentCharity}
                getCharityName={this.getCharityName}
                filtered={!!this.state.startDate || !!this.state.endDate}
                footerMessage={"Donations Selected"}
                getResponse={this.getResponse}
              />
            )}
          </Row>
        </div>
      </main>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  refreshDashboard: () => {
    dispatch(commonActions.refreshDashboard());
  },
});

export default serviceCaller(
  withSuperadminLoginRedirect(
    connect(
      null,
      mapDispatchToProps
    )(
      withUserCharities(
        withModals(withStyles(s, datePickerS)(SuperadminDonationsPage))
      )
    )
  )
);
